/* eslint-disable react-refresh/only-export-components */
//import { StrictMode } from 'react'
import React from 'react';
window.React = React;

// filter console warnings
const originalError = console.error;
console.error = (...args) => {
    const hasRecognize = args.some(arg => typeof arg === 'string' && arg.includes('React does not recognize'));
    const hasAlign = args.some(arg => typeof arg === 'string' && (arg.includes('alignItems') || arg.includes('justifyContent')));
    if(hasRecognize && hasAlign){
        return;
    }
    originalError(...args);
};

const originalWarn = console.warn;
console.warn = (...args) => {
    const hasRecognize = args.some(arg => typeof arg === 'string' && arg.includes('React does not recognize'));
    const hasAlign = args.some(arg => typeof arg === 'string' && (arg.includes('alignItems') || arg.includes('justifyContent')));
    if(hasRecognize && hasAlign){
        return;
    }
    originalWarn(...args);
};

import { createRoot } from 'react-dom/client';
import App from './App.jsx';
import { registerSW } from 'virtual:pwa-register';

// Register Service Worker for PWA
registerSW({ immediate: true });

import { lazy, Suspense } from "react";

// to can use global variables
// eslint gives errors
import axios from 'axios';
window.axios = axios;

// send timezone to backend
import dayjs from 'dayjs';
import timezonePlugin from 'dayjs/plugin/timezone';
dayjs.extend(timezonePlugin);
window.axios.defaults.headers.common['Timezone'] = dayjs.tz.guess();

import { getUser, getToken } from '@/utils/User';
import { getTablesConfig } from '@/utils/Table';
import { defaultParams } from '@/utils/Backend';

// https://www.npmjs.com/package/react-error-boundary
import { ErrorBoundary } from "react-error-boundary";
const Error = lazy(() => import('@/pages/Error'));
import { sendLogs } from '@/utils/Logs';

//import { BrowserRouter } from 'react-router-dom';

import { I18nextProvider } from "react-i18next";
import i18next from "i18next";

//import LanguageDetector from 'i18next-browser-languagedetector';
import { getLanguage, setLanguage } from "@/utils/Cookies";
import { getUserConfig } from "@/utils/User";
import { setDarkMode } from "@/utils/Settings";
import { backendApi } from "@/utils/URL";

// split translations to avoid retranslation every time we develop new code

// ENGLISH
import en_1 from "./translations/en/global.json";
import en_2 from "./translations/en/global2.json";
const translation_en = { ...en_1, ...en_2 };

// SPANISH
import es_1 from "./translations/es/global.json";
import es_2 from "./translations/es/global2.json";
const translation_es = { ...es_1, ...es_2 };

// CATALÀ
import ca_1 from "./translations/ca/global.json";
import ca_2 from "./translations/ca/global2.json";
const translation_ca = { ...ca_1, ...ca_2 };

const defaultLanguage = 'es';

var language = getLanguage();

if(!language || language.length === 0){
    language = defaultLanguage;
    setLanguage(language);
}


i18next
    //.use(LanguageDetector)
    .init({
        debug: false,
        silent: true,
        nsSeparator: false,
        interpolation: { escapeValue: false },  // React already does escaping
        lng: language, // language to use
        resources: {
            en: {
                // 'global' is our custom namespace
                global: translation_en
            },
            es: {
                global: translation_es
            },
            ca: {
                global: translation_ca
            }
        },
});



// what to do if any error
const fallback = ({error /*,resetErrorBoundary*/ }) => {

    // if localhost, print error
    if(window.location.href.includes('localhost') || window.location.href.includes('127.0')){
        console.log(error);
    }

    // Call resetErrorBoundary() to reset the error boundary and retry the render.    
    return (
        <Suspense>
            <Error/>
        </Suspense>
    );
}

// log the error
const logError = (error,info) => {  
    const msg = error && typeof error === 'object' ? (error.message || JSON.stringify(error)) : String(error);
    sendLogs(language, msg, info?.componentStack);
};

// Catch unhandled promise rejections (like inside axios .catch)
window.addEventListener('unhandledrejection', function(event) {
    var error = event.reason;
    var message = error && error.message ? error.message : String(error);
    var stack = error && error.stack ? error.stack : '';
    logError({ message: 'Unhandled Promise Rejection: ' + message }, { componentStack: stack });
});

// Catch other async/event listener errors
window.addEventListener('error', function(event) {
    var message = event.message || String(event.error);
    var stack = event.error && event.error.stack ? event.error.stack : '';
    logError({ message: 'Uncaught Error: ' + message }, { componentStack: stack });
});

const handleLanguage = (iso) => {  

    const reload = true;
    
    // change on app
    i18next.changeLanguage(iso); 

    // save on local storage
    setLanguage(iso);

    // save on server on the user config
    const user = getUser();
    const token = getToken();

    // get config values in json format
    let currentConfig = {};
    try {
        currentConfig = JSON.parse(getUserConfig(null, true));
    } catch(e) {
        currentConfig = {};
    }

    if(user && token){

        const localTablesObj = getTablesConfig();
        const userTables = localTablesObj[user.id] || {};

        const sendConfig = {
            user: currentConfig
        };

        if(Object.keys(userTables).length > 0){
            sendConfig.tables = userTables;
        }

        const apiUrl = backendApi();

        const apiPath = '/user/preferences';

        const apiConfig= {
            headers: { Authorization: `Bearer ${token}` } 
        };

        const defaults = defaultParams(iso,user);

        const send = {
            ...defaults,
            id: user?.id,
            config: JSON.stringify(sendConfig)
        };

        axios.post(apiUrl+apiPath,send,apiConfig)
        .then(function () {
            if(reload){
                window.location.reload();
            }
        })
        .catch(function (){     
            if(reload){                         
                window.location.reload();            
            }
        });
    }
    else {
        if(reload){
            window.location.reload();
        }
    }
};

const handleDarkMode = (isDark) => {  
    
    // save on local storage and apply class
    setDarkMode(isDark);

    // save on server on the user config
    const user = getUser();
    const token = getToken(); // Use the centralized getToken helper
    let currentConfig = {};
    try {
        currentConfig = JSON.parse(getUserConfig(null, true));
    } catch(e) {
        currentConfig = {};
    }

    if(user && token){

        const localTablesObj = getTablesConfig();
        const userTables = localTablesObj[user.id] || {};

        const sendConfig = {
            user: currentConfig
        };

        if(Object.keys(userTables).length > 0){
            sendConfig.tables = userTables;
        }

        const apiUrl = backendApi();
        const apiPath = '/user/preferences';

        const apiConfig= {
            headers: { Authorization: `Bearer ${token}` } 
        };

        const defaults = defaultParams(i18next.language,user);

        const send = {
            ...defaults,
            id: user?.id,
            config: JSON.stringify(sendConfig)
        };

        axios.post(apiUrl+apiPath,send,apiConfig)
        .then(function () {
            // silent save
        })
        .catch(function (error){                              
            console.error("Error saving darkmode config:", error);
        });
    }
};

createRoot(document.getElementById('root')).render(
    //<StrictMode> if commented, avoiding calling useEffect twice
    //      <BrowserRouter>
    <I18nextProvider i18n={i18next}>
        <ErrorBoundary FallbackComponent={fallback} onError={logError}>
                <App 
                    handleLanguage={handleLanguage}
                    handleDarkMode={handleDarkMode}
                />
        </ErrorBoundary>
    </I18nextProvider>
    //      </BrowserRouter>
    //</StrictMode>,
)
